import type { BuiltinEnvironmentSeed, EnvironmentConfig, EnvironmentSource } from './types';

/** localStorage 存储键 */
export const STORAGE_KEYS = {
  /** 用户手动添加的环境列表 */
  localEnvironments: 'er-console.env.local',
  /** envId -> API Key */
  apiKeys: 'er-console.env.apiKeys',
  /** 当前激活的环境 id */
  activeId: 'er-console.env.activeId',
} as const;

/** 健康检查超时（毫秒） */
export const HEALTH_TIMEOUT_MS = 10_000;
/** 健康检查轮询间隔（毫秒） */
export const HEALTH_POLL_MS = 30_000;

/** 将名称转为安全的 id 片段 */
export function slugify(name: string): string {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
    .replace(/[^a-z0-9\u4e00-\u9fa5-]/g, '')
    .replace(/-+/g, '-')
    .slice(0, 32);
  return slug || 'env';
}

function randomSuffix(): string {
  return Math.random().toString(36).slice(2, 8);
}

/** 基于名称生成 id，若已存在则追加随机后缀去重 */
export function makeId(name: string, source: EnvironmentSource, existing: string[]): string {
  const base = `${source === 'builtin' ? 'builtin' : 'local'}-${slugify(name)}`;
  if (!existing.includes(base)) return base;
  return `${base}-${randomSuffix()}`;
}

/**
 * 解析构建期注入的环境变量 ER_CONSOLE_ENVIRONMENTS（JSON 数组字符串）。
 * 由 config/config.ts 在构建期通过 define 写入，未配置时为 '[]'。
 */
export function parseBuiltinEnvironments(): EnvironmentConfig[] {
  const raw =
    typeof process !== 'undefined' ? (process.env.ER_CONSOLE_ENVIRONMENTS ?? '[]') : '[]';
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    const seeds = parsed.filter(
      (item): item is BuiltinEnvironmentSeed =>
        typeof item === 'object' &&
        item !== null &&
        typeof (item as BuiltinEnvironmentSeed).name === 'string' &&
        ((item as BuiltinEnvironmentSeed).name as string).length > 0 &&
        typeof (item as BuiltinEnvironmentSeed).baseUrl === 'string' &&
        ((item as BuiltinEnvironmentSeed).baseUrl as string).length > 0,
    );
    const ids: string[] = [];
    return seeds.map((seed) => {
      const id = makeId(seed.name, 'builtin', ids);
      ids.push(id);
      return { id, name: seed.name, description: seed.description, baseUrl: seed.baseUrl, source: 'builtin' };
    });
  } catch (error) {
    console.warn(
      `[ER-Console] 内置环境解析失败: ${error instanceof Error ? error.message : String(error)}`,
    );
    return [];
  }
}

/** 统一校验 baseUrl，追加缺失的协议前缀 */
export function normalizeBaseUrl(input: string): string {
  const trimmed = input.trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}